import React from 'react';
import { Button } from '@components/Button';
import { Flex } from '@components/Flex';
import { Columns } from '@components/Columns';
import { CTALink } from '@components/CTALink';
import { JoykitIcon } from '@components/JoykitIcon';
import { ParticleJoyLogo } from './ParticleJoyLogo';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { getRootUrl } from '@utils/urlHelpers.util';
import {
  FooterColumns,
  FooterHeroMenuItem,
  FooterSubHeroItem,
  FooterSubHeroItemText,
  FooterSubHeroItems,
  SocialMediaMenuList,
  FooterColumn,
  CTALinkHash,
  CTALinkHashMobile,
  BottomMenuList,
  BottomMenuListMobile,
  BottomText,
  BottomCTALink,
  BottomPrivacyCTALink,
} from './Footer.styles';
import { Box } from '@components/Box';
import { CookiesConsentBanner, CookiesConsentBannerStyles } from './CookiesConsentBanner';

const footerPageId = ({ page }: { page?: MarcomPageName }) => {
  switch (page) {
    case 'home':
      return 'Home_Footer';
    case 'wedding-website':
      return 'Wedding_Websites_Footer';
    case 'guest-list':
      return 'Guest_List_Footer';
    case 'online-rsvp':
      return 'RSVP_Footer';
    case 'save-the-date':
      return 'Save_the_Dates_Footer';
    case 'registry':
      return 'Registry_Footer';
    case 'app':
      return 'App_Footer';
    default:
      return 'mainLayoutFooter';
  }
};

const appGetStartedMenuContent = ({
  page,
  handleGetStarted,
  bottomLeftId = '',
}: {
  page?: MarcomPageName;
  handleGetStarted?: (e: React.MouseEvent) => void;
  bottomLeftId?: string;
}) => {
  return (
    <>
      <GetStartedButton
        id={`footer2create${bottomLeftId}`}
        page={page}
        className="get-started-button"
        variant="prime"
        onClick={handleGetStarted}
      >
        Get Started
      </GetStartedButton>
      <Box marginTop={[5, 0]}>
        <Button
          id={`footer2login${bottomLeftId}`}
          href={getRootUrl('/login')}
          forwardedAs={'a'}
          className="bottom-footer-link footer-login-button"
          variant="altPurpleSecondary"
          onClick={handleGetStarted}
        >
          Sign In
        </Button>
      </Box>
      <CTALink
        id={`footer2loginLink${bottomLeftId}`}
        href={getRootUrl('/login')}
        className="bottom-footer-link footer-login-link"
        variant="altPurple"
        onClick={handleGetStarted}
      >
        Sign In
      </CTALink>
      <Box marginTop={[6, 0]}>
        <CTALink id={`footer2find${bottomLeftId}`} href={getRootUrl('/find')} className="bottom-footer-link" variant="altPurple">
          Find an Event
        </CTALink>
      </Box>
    </>
  );
};

const FooterBottomMenu: React.FC<{ mobileId?: string }> = ({ mobileId = '' }) => {
  return (
    <>
      <Flex alignItems="center">
        <BottomPrivacyCTALink id={`footer2Terms${mobileId}`} href="https://withjoy.com/eula" variant="footerAlt">
          Terms
        </BottomPrivacyCTALink>
        <BottomText tagName="span" color="footerText" variant="featureHed17">
          &
        </BottomText>
        <BottomPrivacyCTALink id={`footer2privacy${mobileId}`} href="https://privacy.withjoy.com" variant="footerAlt">
          Privacy
        </BottomPrivacyCTALink>
      </Flex>
      <BottomCTALink
        id={`footerPrivacyNotice${mobileId}`}
        href="https://privacy.withjoy.com/privacy-policy#california-privacy-notice"
        variant="footerAlt"
        noreferrer
        noopener
      >
        California Privacy Notice
      </BottomCTALink>
      <BottomCTALink
        id={`footerDonotShareNotice${mobileId}`}
        href="https://privacy.withjoy.com/opt-out"
        variant="footerAlt"
        noreferrer
        noopener
      >
        Do Not Sell or Share My Personal Information
      </BottomCTALink>
      <CookiesConsentBanner />
    </>
  );
};

export const Footer: React.FC<{ page?: MarcomPageName; handleGetStarted?: (e: React.MouseEvent) => void }> = ({
  page,
  handleGetStarted,
}) => {
  return (
    <footer id={footerPageId({ page })}>
      <div className="footer-menu-container">
        <FooterColumns>
          <FooterColumn desktop="half" mobile="full">
            <div className="footer-hero-menu">
              <FooterHeroMenuItem>
                <CTALink id="footer2weddingWebsite" href="/wedding-website" variant="footerAlt">
                  Wedding Website
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2registry" href="/registry" variant="footerAlt">
                  Registry
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2guestList" href="/guest-list" variant="footerAlt">
                  Guest List
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2saveTheDate" href="/save-the-date" variant="footerAlt">
                  Save the Dates
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2weddingInvitations" href="/wedding-invitations" variant="footerAlt">
                  Invitations
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2OnlineRsvp" href="/online-rsvp" variant="footerAlt">
                  Smart RSVP
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2mobileApp" href="/app" variant="footerAlt">
                  Mobile App
                </CTALink>
              </FooterHeroMenuItem>
            </div>
            <div className="footer-app-get-started-menu">{appGetStartedMenuContent({ page, handleGetStarted })}</div>
          </FooterColumn>
          <FooterColumn desktop="half" mobile="full">
            <Flex justifyContent={'flex-end'}>
              <div className="footer-sub-hero-menus-container">
                <div className="footer-sub-hero-menu">
                  <FooterSubHeroItem>
                    <FooterSubHeroItemText marginTop={[8]} tagName="h2" color="footerText" variant="featureHed20">
                      Resources
                    </FooterSubHeroItemText>
                  </FooterSubHeroItem>
                  <FooterSubHeroItems>
                    <CTALink id="footer2designs" href="/designs" variant="footerAlt">
                      Design Templates
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2inspiration" href="https://withjoy.com/blog/" variant="footerAlt">
                      Ideas & Inspiration
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2faq" href="/faq" variant="footerAlt">
                      FAQs
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2help" href="https://help.withjoy.com" variant="footerAlt">
                      Help & Support
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2pricing" href="/pricing" variant="footerAlt">
                      Pricing
                    </CTALink>
                  </FooterSubHeroItems>
                </div>
                <div className="footer-sub-hero-menu" style={{ paddingBottom: '49px' }}>
                  <FooterSubHeroItem>
                    <FooterSubHeroItemText marginTop={[8]} tagName="h2" color="footerText" variant="featureHed20">
                      About Joy
                    </FooterSubHeroItemText>
                  </FooterSubHeroItem>
                  <FooterSubHeroItems>
                    <CTALink id="footer2about" href="/our-story" variant="footerAlt">
                      Our Story
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2careers" href="/careers" variant="footerAlt">
                      Careers
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2newsroom" href="/newsroom" variant="footerAlt">
                      Newsroom
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2contactus" href="mailto:hello@withjoy.com" variant="footerAlt">
                      Contact Us
                    </CTALink>
                  </FooterSubHeroItems>
                  <BottomMenuListMobile>
                    <FooterBottomMenu mobileId={'Mobile'} />
                  </BottomMenuListMobile>
                </div>
                <div className="footer-top-logo-wrapper">
                  <ParticleJoyLogo id="footer2brand-mobile" ariaLabel="Joy Logo" variant="primary" />
                </div>

                <SocialMediaMenuList>
                  <CTALink id="footer2instagram" href="https://www.instagram.com/withjoy" className="social-media-link" variant="footerAlt">
                    <JoykitIcon name="instagramSocial" color="linkFooterHover" />
                  </CTALink>
                  <CTALink
                    id="footer2pinterest"
                    href="https://www.pinterest.com/joytheapp"
                    className="social-media-link"
                    variant="footerAlt"
                  >
                    <JoykitIcon name="pinterest" color="linkFooterHover" />
                  </CTALink>
                  <CTALink id="footer2facebook" href="https://www.facebook.com/joytheapp" className="social-media-link" variant="footerAlt">
                    <JoykitIcon name="facebook" color="linkFooterHover" />
                  </CTALink>
                  <CTALink
                    id="footer2twitter"
                    href="https://twitter.com/joytheapp"
                    className="social-media-link twitter-link"
                    variant="footerAlt"
                  >
                    <JoykitIcon name="twitter" color="linkFooterHover" />
                  </CTALink>
                  <CTALinkHash
                    id="footer2twitterHash"
                    href="https://twitter.com/joytheapp"
                    className="social-media-link twitter-link"
                    variant="footerAlt"
                  >
                    #withjoy
                  </CTALinkHash>
                </SocialMediaMenuList>
                <CTALinkHashMobile
                  id="footer2twitterHashMobile"
                  href="https://twitter.com/joytheapp"
                  className="twitter-link"
                  variant="footerAlt"
                >
                  #withjoy
                </CTALinkHashMobile>
              </div>
            </Flex>
          </FooterColumn>
        </FooterColumns>
      </div>

      <div className="footer-bottom-menu">
        <Columns>
          <Columns.Column width={8}>
            <div className="footer-bottom-menu-left">
              {appGetStartedMenuContent({ page, handleGetStarted, bottomLeftId: 'BottomLeft' })}
            </div>
          </Columns.Column>
          <Columns.Column width={4}>
            <div className="footer-bottom-menu-right">
              <ParticleJoyLogo id="footer2brand-desktop" variant="primary" ariaLabel="Footer Desktop Joy Logo" />
            </div>
          </Columns.Column>
        </Columns>
      </div>

      <BottomMenuList>
        <FooterBottomMenu />
      </BottomMenuList>
      <CookiesConsentBannerStyles />
    </footer>
  );
};
